import React from "react";
import { motion } from "framer-motion";
import { Star, Check, ShieldCheck } from "lucide-react";
import { SITE, whatsappLink } from "@/data/site";
import { useAvailability, urgencyBadge } from "@/hooks/useAvailability";
import { useProgram } from "@/lib/programs";
import { czk } from "@/lib/price";

export default function Hero() {
  const availability = useAvailability();
  const { program, byLocation, differs, location, setLocation } = useProgram();
  const bullets = program.items?.length ? program.items.map((i) => ({ label: i.label, price: i.price ? czk(i.price) : "" })) : SITE.hero.bullets;
  const sub = program.id ? `${program.subtitle || SITE.hero.sub} Vše za ${czk(program.price)}.`.replace(/\.\s*Vše za/, ". Vše za") : SITE.hero.sub;
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-black"
    >
      {/* Background image with ken-burns */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          initial={{ scale: 1.05 }}
          animate={{ scale: 1.18 }}
          transition={{ duration: 24, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${SITE.images.heroBg})` }}
        />
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/50" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black" />
      <div className="absolute inset-0 grain pointer-events-none" />

      {/* Top urgency badge */}
      <div className="absolute top-20 lg:top-24 inset-x-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div
            data-testid="hero-urgency-badge"
            className="inline-flex items-center gap-2 bg-brand-accent text-black font-semibold px-3 py-1.5 uppercase tracking-[0.15em] text-[10px]"
          >
            <span className="w-1.5 h-1.5 bg-black rounded-full animate-pulse" />
            {urgencyBadge(availability)}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-[100svh] flex flex-col justify-center pt-32 pb-32 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT: Emotional copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-brand-accent mb-6">
              {SITE.hero.eyebrow}
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display font-black text-4xl sm:text-5xl lg:text-7xl xl:text-[5.5rem] leading-[0.95] tracking-tight"
            >
              {SITE.hero.headlineLines.map((line, i) => (
                <span key={i} className="block">
                  {i === SITE.hero.headlineLines.length - 1 ? (
                    <span className="italic font-light text-brand-accent">{line}</span>
                  ) : (
                    line
                  )}
                </span>
              ))}
            </h1>

            <p className="mt-8 max-w-2xl text-base lg:text-xl text-white/80 leading-relaxed">
              {sub}
            </p>

            {/* Trust row */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/70">
              {SITE.hero.trust.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check size={14} className="text-brand-accent" />
                  <span>{t}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#reservation"
                data-testid="hero-book-button"
                className="group inline-flex flex-col items-start gap-0.5 bg-brand-accent text-black font-semibold pl-7 pr-6 py-3.5 hover:bg-brand-accentHover transition-all"
              >
                <span className="flex items-center gap-3 text-base">
                  Objednat za {czk(program.price)}
                  <span className="inline-block transition-transform group-hover:translate-x-1.5">
                    →
                  </span>
                </span>
                <span className="text-[10px] font-normal uppercase tracking-[0.15em] opacity-70">
                  {SITE.hero.ctaSub}
                </span>
              </a>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp"
                className="inline-flex items-center gap-3 px-7 py-4 border border-white/20 text-white hover:border-brand-accent hover:text-brand-accent transition-all"
              >
                Napsat na WhatsApp
              </a>
            </div>
          </motion.div>

          {/* RIGHT: Offer card */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <div className="relative">
              {/* Glow */}
              <div className="absolute -inset-px bg-gradient-to-br from-brand-accent/50 via-transparent to-brand-accent/20 blur-md opacity-60" />
              <div
                data-testid="hero-offer-card"
                className="relative bg-black/85 backdrop-blur-xl border border-brand-accent/30 p-6 lg:p-8"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted" data-testid="hero-program-title">
                    {program.title} — vše v jedné ceně
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] bg-brand-accent text-black px-2 py-0.5">
                    -{program.discount_pct} %
                  </span>
                </div>
                {differs && (
                  <div className="flex gap-1 mb-4" data-testid="hero-program-location">
                    {["cestlice", "butovice"].map((k) => (
                      <button key={k} type="button" onClick={() => setLocation(k)} data-testid={`hero-program-location-${k}`}
                        className={`h-8 px-3 text-[11px] uppercase tracking-[0.15em] border transition-colors ${location === k ? "bg-brand-accent text-black border-brand-accent" : "border-white/20 text-white/70 hover:text-white"}`}>
                        {k === "cestlice" ? "Čestlice" : "Butovice"}{byLocation[k] && byLocation[k].id !== program.id ? ` · ${czk(byLocation[k].price)}` : ""}
                      </button>
                    ))}
                  </div>
                )}

                {/* Items with crossed prices */}
                <ul className="space-y-3 mb-6">
                  {bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-3 pb-3 border-b border-white/5 last:border-0"
                    >
                      <div className="flex items-start gap-2.5 min-w-0">
                        <Check size={15} className="mt-0.5 shrink-0 text-brand-accent" />
                        <span className="text-sm text-white/90">{b.label}</span>
                      </div>
                      <span className="font-display text-sm text-brand-muted line-through shrink-0">
                        {b.price}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Total row */}
                <div className="flex items-center justify-between pb-4 border-b border-white/10">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-muted">
                    Cena jednotlivě
                  </span>
                  <span className="font-display text-lg text-brand-muted line-through">
                    {czk(program.regular_price_effective)}
                  </span>
                </div>

                {/* Bundle price */}
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-brand-accent">
                      Vaše cena
                    </div>
                    <div className="mt-1 text-xs text-white/60">hotovo do 1 dne</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-4xl lg:text-5xl text-brand-accent leading-none" data-testid="hero-program-price">
                      {czk(program.price)}
                    </div>
                    <div className="mt-1 text-[11px] font-medium text-brand-accent">
                      Ušetříte {czk(program.savings)}
                    </div>
                  </div>
                </div>

                {/* CTA inside card */}
                <a
                  href="#reservation"
                  data-testid="hero-offer-cta"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-accent text-black font-semibold py-3.5 hover:bg-brand-accentHover transition-all"
                >
                  Chci ušetřit {czk(program.savings)} →
                </a>

                {/* Mini trust */}
                <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-white/60">
                  <ShieldCheck size={13} className="text-brand-accent" />
                  <span>Spokojenost garantovaná — nebo peníze zpět</span>
                </div>
              </div>

              {/* Rating mini */}
              <div className="absolute -top-3 left-6 flex items-center gap-1.5 bg-black border border-brand-accent/40 px-3 py-1.5">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={10} className="fill-brand-accent text-brand-accent" />
                  ))}
                </div>
                <span className="text-[10px] font-bold text-white">4.9 · 500+ recenzí</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
